import type {
  AddSubscriptionTiersRequestBody,
  SubscriptionTierEntity,
  UpdateSubscriptionTiersRequestBody,
  UpdateSingleSubscriptionTierPayload,
} from "../../../react-query-hooks/store/subscriptions/tiers/types";
import type {EditSubscriptionTierFormRow} from "./types";
import {tierFormRowToAddTierPayload} from "../subscription-tier-form-to-api-payload";
import {mapTierEntityToFormRow} from "./map-subscription-to-form";

function addPayloadToUpdatePayload(
  p: ReturnType<typeof tierFormRowToAddTierPayload>,
  id?: number
): UpdateSingleSubscriptionTierPayload & {id?: number} {
  return {
    name: p.name,
    billingInterval: p.billingInterval,
    billingDuration: p.billingDuration,
    billingCycles: p.billingCycles,
    features: p.features,
    sortOrder: p.sortOrder,
    isPayWhatYouWant: p.isPayWhatYouWant,
    ...(p.defaultMinPrice === undefined ? {} : {defaultMinPrice: p.defaultMinPrice}),
    currencies: p.currencies ?? [],
    selectedCurrencies: p.selectedCurrencies ?? [],
    ...(p.redirectType
      ? {
          redirectType: p.redirectType,
          telegramGroupChatId: p.telegramGroupChatId,
          urlRedirect: p.urlRedirect,
        }
      : {}),
    ...(typeof id === "number" ? {id} : {}),
  };
}

/**
 * A saved tier as the update the form would send for it untouched.
 *
 * Built through the same path as the form's own tiers (saved tier → form row → payload)
 * rather than by hand, so the two sides of the comparison below cannot drift: a field the
 * form path adds or drops, such as a Minimum Price, is added or dropped on both.
 */
function originalTierToUpdatePayload(
  tier: SubscriptionTierEntity,
  sortOrder: number
): UpdateSingleSubscriptionTierPayload & {id?: number} {
  return addPayloadToUpdatePayload(
    tierFormRowToAddTierPayload(mapTierEntityToFormRow(tier), sortOrder),
    tier.id
  );
}

function comparablePayload(value: unknown) {
  return JSON.stringify(value);
}

/**
 * react-hook-form does not register `apiTierId`, so it can be stripped from `formTiers`.
 * Recover the id from the loaded API tiers by matching tier name (trim + lower-case),
 * so the sync below does not POST a duplicate row for a tier that already exists.
 */
function resolveApiTierId(
  formRow: EditSubscriptionTierFormRow,
  originalTiers: SubscriptionTierEntity[]
): number | undefined {
  if (typeof formRow.apiTierId === "number") {
    return formRow.apiTierId;
  }
  const formName = (formRow.tierName ?? "").trim().toLowerCase();
  if (!formName) {
    return undefined;
  }
  return originalTiers.find((tier) => tier.name.trim().toLowerCase() === formName)?.id;
}

/**
 * Applies tier list from the form vs loaded subscription: deletes removed tiers,
 * updates existing, adds new rows (no apiTierId).
 */
export async function syncEditSubscriptionTiers(params: {
  subscriptionId: number;
  formTiers: EditSubscriptionTierFormRow[];
  originalTiers: SubscriptionTierEntity[];
  addTiers: (args: {
    subscriptionId: number;
    body: AddSubscriptionTiersRequestBody;
  }) => Promise<unknown>;
  updateTiers: (args: {
    subscriptionId: number;
    body: UpdateSubscriptionTiersRequestBody;
  }) => Promise<unknown>;
}): Promise<void> {
  const {subscriptionId, formTiers, originalTiers, addTiers, updateTiers} = params;

  const submittedTiers = formTiers.map((row, index) => {
    const resolvedId = resolveApiTierId(row, originalTiers);
    return {
      formRow: row,
      resolvedId,
      addPayload: tierFormRowToAddTierPayload(row, index),
      updatePayload: addPayloadToUpdatePayload(
        tierFormRowToAddTierPayload(row, index),
        resolvedId
      ),
    };
  });

  const originalPayload = originalTiers.map((tier, index) =>
    originalTierToUpdatePayload(tier, index)
  );

  if (
    comparablePayload(submittedTiers.map((tier) => tier.updatePayload)) ===
    comparablePayload(originalPayload)
  ) {
    return;
  }

  const newTiers = submittedTiers
    .filter((tier) => typeof tier.resolvedId !== "number")
    .map((tier) => tier.addPayload);

  if (newTiers.length > 0) {
    await addTiers({
      subscriptionId,
      body: {tiers: newTiers},
    });
  }

  const changedExistingTiers = submittedTiers
    .filter((tier) => typeof tier.resolvedId === "number")
    .map((tier) => tier.updatePayload)
    .filter((tier) => {
      const original = originalPayload.find((item) => item.id === tier.id);
      return comparablePayload(tier) !== comparablePayload(original);
    });

  if (changedExistingTiers.length > 0) {
    await updateTiers({
      subscriptionId,
      body: {tiers: changedExistingTiers},
    });
  }
}
